import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight, MessageCircle, Check, X, ShoppingCart, Plus, Minus, Zap } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductPage = () => {
  const { productId } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInquiry, setShowInquiry] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: inquiryForm.name,
        email: inquiryForm.email,
        subject: `Inquiry about: ${product.name}`,
        message: inquiryForm.message,
        product_id: product.id,
      });
      toast.success("Your inquiry has been sent!");
      setShowInquiry(false);
      setInquiryForm({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-2xl text-stone-900 mb-4">Product not found</h1>
          <Link
            to="/shop"
            className="text-deep-moss font-sans hover:underline"
          >
            Back to shop
          </Link>
        </div>
      </div>
    );
  }

  const gallery =
    product.images && product.images.length > 0
      ? product.images
      : [product.image_url].filter(Boolean);

  return (
    <div className="animate-fade-in">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4">
          <nav className="flex items-center gap-2 text-sm font-sans" data-testid="breadcrumb">
            <Link to="/" className="text-stone-500 hover:text-deep-moss">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <Link to="/shop" className="text-stone-500 hover:text-deep-moss">
              Shop
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <Link to={`/shop/${product.category}`} className="text-stone-500 hover:text-deep-moss capitalize">
              {product.category.replace("-", " ")}
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <span className="text-stone-900 truncate max-w-[150px]">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-12">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16">
          {/* Product Images */}
          <div>
            <div className="aspect-square overflow-hidden bg-white border border-stone-200">
              <img
                src={gallery[activeImage] || product.image_url}
                alt={product.name}
                data-testid="product-image"
                className="w-full h-full object-cover"
              />
            </div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3" data-testid="product-thumbnails">
                {gallery.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImage(idx)}
                    data-testid={`product-thumbnail-${idx}`}
                    className={`aspect-square overflow-hidden bg-white border rounded transition-all ${
                      activeImage === idx
                        ? "border-deep-moss ring-2 ring-deep-moss/30"
                        : "border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    <img src={img} alt={`${product.name} view ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              {product.is_new && <span className="badge-new">New</span>}
              {product.is_featured && <span className="badge-featured">Featured</span>}
              {product.product_type === "digital" && (
                <span
                  data-testid="digital-badge"
                  className="inline-flex items-center gap-1 bg-deep-moss text-white text-xs font-medium px-2 py-1 rounded"
                >
                  <Zap className="w-3 h-3" /> Digital · Instant Download
                </span>
              )}
            </div>

            <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
              {product.category.replace("-", " ")}
            </p>

            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900 mb-4" data-testid="product-name">
              {product.name}
            </h1>

            <p className="font-sans text-2xl text-deep-moss font-semibold mb-6" data-testid="product-price">
              ${product.price.toFixed(2)}
            </p>

            <div className="flex items-center gap-2 mb-6">
              {product.product_type === "digital" ? (
                <div className="flex items-center gap-1 text-deep-moss">
                  <Zap className="w-4 h-4" />
                  <span className="font-sans text-sm">Instant digital download after purchase — no shipping</span>
                </div>
              ) : product.in_stock ? (
                <div className="flex items-center gap-1 text-green-700">
                  <Check className="w-4 h-4" />
                  <span className="font-sans text-sm">In Stock</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-red-600">
                  <X className="w-4 h-4" />
                  <span className="font-sans text-sm">Out of Stock</span>
                </div>
              )}
            </div>

            <div className="border-t border-stone-200 pt-6 mb-8">
              <h2 className="font-serif text-lg font-medium text-stone-900 mb-3">Description</h2>
              <p className="font-sans text-stone-600 leading-relaxed whitespace-pre-line" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-4 mt-auto">
              {/* Quantity Selector */}
              <div className="flex items-center gap-4">
                <span className="font-sans text-sm text-stone-600">Quantity:</span>
                <div className="flex items-center border border-stone-200">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="decrease-qty"
                    className="p-2 hover:bg-stone-100 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-4 font-sans text-sm">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="increase-qty"
                    className="p-2 hover:bg-stone-100 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={() => {
                  addItem(product, quantity);
                  toast.success(`${product.name} added to cart!`);
                }}
                disabled={!product.in_stock}
                data-testid="add-to-cart-btn"
                className="flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-8 py-4 font-sans font-medium transition-transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="w-5 h-5" />
                Add to Cart
              </button>

              <button
                onClick={() => setShowInquiry(true)}
                data-testid="ask-question-btn"
                className="flex items-center justify-center gap-2 border border-deep-moss text-deep-moss hover:bg-deep-moss hover:text-white rounded-full px-8 py-4 font-sans font-medium transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Ask a Question
              </button>
              <Link
                to="/contact"
                data-testid="contact-link"
                className="text-center text-deep-moss font-sans text-sm hover:underline"
              >
                Or contact us for more information
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      {showInquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white w-full max-w-md p-8 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-xl font-semibold text-stone-900">
                Ask About This Product
              </h3>
              <button
                onClick={() => setShowInquiry(false)}
                data-testid="close-inquiry-modal"
                className="text-stone-500 hover:text-stone-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="font-sans text-sm text-stone-600 mb-6">
              Have questions about <strong>{product.name}</strong>? Send us a message and we'll get back to you soon.
            </p>

            <form onSubmit={handleInquirySubmit} className="space-y-4">
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">Name</label>
                <input
                  type="text"
                  value={inquiryForm.name}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                  data-testid="inquiry-name"
                  className="w-full input-underline"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">Email</label>
                <input
                  type="email"
                  value={inquiryForm.email}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                  data-testid="inquiry-email"
                  className="w-full input-underline"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">Message</label>
                <textarea
                  value={inquiryForm.message}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                  data-testid="inquiry-message"
                  rows={4}
                  className="w-full input-underline resize-none"
                  placeholder="What would you like to know?"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit-btn"
                className="w-full bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-6 py-3 font-sans font-medium transition-colors disabled:opacity-50"
              >
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
